import * as React from 'react'
import { consoleWarn } from '../../logging'
import { bodyScroll } from '../../utilities'

/**
 * Warns if the Modal component is unmounted while `visible` is `true`.
 * @param visible - Indicates if the modal is visible.
 */
export function useUnmountWarning(visible: boolean) {
  const isVisible = React.useRef<boolean>(false)
  isVisible.current = visible

  React.useEffect(
    () => () => {
      if (isVisible.current) {
        // https://docusign.atlassian.net/browse/FRNTEND-4854
        consoleWarn(
          'The Modal component was unmounted while `visible` was `true`. This will prevent exit animation and may have unintended effects for keyboard users.',
        )
      }
    },
    [],
  )
}

/**
 * Saves the element that was focused when visible was set to true and
 * returns focus to it when visible is set to false.
 *
 * @param visible - Indicates if the modal is visible.
 * @returns An object containing a function to manually return focus to the previous element.
 */
export function useFocusReturn(visible: boolean) {
  const previousFocusRef = React.useRef<HTMLElement | null>(null)
  React.useEffect(() => {
    if (visible) {
      if (
        !previousFocusRef.current &&
        document.activeElement instanceof HTMLElement
      ) {
        previousFocusRef.current = document.activeElement
      }
    } else if (!visible) {
      if (
        previousFocusRef.current &&
        document.body.contains(previousFocusRef.current)
      ) {
        previousFocusRef.current.focus()
      }

      previousFocusRef.current = null
    }
  }, [visible])

  /** Safety: Ensure focus is returned on unmount */
  React.useEffect(
    () => () => {
      if (
        previousFocusRef.current &&
        document.body.contains(previousFocusRef.current)
      ) {
        previousFocusRef.current.focus()
        previousFocusRef.current = null
      }
    },
    [],
  )

  const focusPrevious = React.useCallback(() => {
    if (
      previousFocusRef.current &&
      document.body.contains(previousFocusRef.current)
    ) {
      previousFocusRef.current.focus()
    }
  }, [])

  return { focusPrevious }
}

/**
 * Calls the onOpen callback when the modal becomes visible and mounted
 *
 * This hook ensures that the latest value of onOpen is used  without causing
 * unnecessary executions of the effect.
 *
 * @param modalElement - The modal element.
 * @param visible - Indicates if the modal is visible.
 * @param onOpen - Callback function to be called when the modal becomes visible.
 */
export function useOnOpenCallback(
  modalElement: HTMLElement | null,
  visible: boolean,
  onOpen?: () => void,
) {
  const onOpenRef = React.useRef(onOpen)
  React.useEffect(() => {
    onOpenRef.current = onOpen
  }, [onOpen])

  React.useEffect(() => {
    if (visible && modalElement) {
      onOpenRef.current?.()
    }
  }, [modalElement, onOpenRef, visible])
}

/**
 * Disables body scroll when disableScroll is true, and re-enables
 * scroll when disableScroll is false. Ensures scroll is re-enabled on unmount.
 *
 * @param scrollableElement - The element whose scroll should be managed.
 * @param disableScroll - Flag indicating whether to disable scroll.
 */
export function useDisableBodyScroll(
  scrollableElement: HTMLElement | null,
  disableScroll = false,
) {
  const bodyScrollElement = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    if (disableScroll && scrollableElement) {
      bodyScrollElement.current = scrollableElement
      bodyScroll.disable(bodyScrollElement.current)
    } else if (!disableScroll && bodyScrollElement.current) {
      bodyScroll.enable(bodyScrollElement.current)
      bodyScrollElement.current = null
    }
  }, [scrollableElement, disableScroll])

  /** Safety: Ensure scroll is re-enabled on unmount */
  React.useEffect(
    () => () => {
      if (bodyScrollElement.current) {
        bodyScroll.enable(bodyScrollElement.current)
        bodyScrollElement.current = null
      }
    },
    [],
  )
}
